import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import { translateError, handleUnauthorizedError } from '@/lib/errorTranslation';
import { LanguageToggle } from '@/components/LanguageToggle';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { ArrowLeft, Download, Calendar, ClipboardList, Syringe, Scale, Ruler, AlertCircle } from 'lucide-react';

interface Visit {
  id: number;
  visit_date: string;
  weight_kg?: number;
  height_cm?: number;
  parent_notes?: string;
  doctor_name: string;
  doses?: Array<{ dose_number: number; vaccine_label: string }>;
}

interface Patient {
  id: number;
  name: string;
  dob: string;
  doctor_name: string;
  clinic_name: string;
  upcoming_doses?: any[];
  completed_doses?: any[];
}

/**
 * Parse a date string (YYYY-MM-DD) as local time, not UTC.
 */
function parseLocalDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  if (dateStr.includes('T') || dateStr.includes(' ')) {
    return new Date(dateStr.replace(' ', 'T'));
  }
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function ParentPatientDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, isLoading } = useAuth0();
  
  const [patient, setPatient] = useState<Patient | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('visits');
  const [useMetric, setUseMetric] = useState(true);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/parent/login');
      return;
    }
    loadData();
  }, [isAuthenticated, isLoading, navigate, id]);

  const loadData = async () => {
    try {
      if (!id) return;

      setLoading(true);

      const [patientResult, visitsResult]: any[] = await Promise.all([
        api.getParentPatient(Number(id)),
        api.getParentPatientVisits(Number(id)),
      ]);

      setPatient(patientResult);
      setVisits(visitsResult?.visits || []);
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'parent')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPassport = async () => {
    if (!patient) return;
    try {
      const blob = await api.downloadVaccinePassport(patient.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const today = new Date().toISOString().split('T')[0];
      const safeName = patient.name
        .replaceAll(/[^a-zA-Z0-9\s]/g, '')
        .replaceAll(/\s+/g, '-')
        .toLowerCase()
        .trim();
      a.download = `cartilla-de-vacunacion-${safeName || 'paciente'}-${today}.pdf`;
      
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();

      toast({
        title: t('app.success'),
        description: t('parent.downloadPassport'),
      });
    } catch (error: any) {
      if (handleUnauthorizedError(error, navigate, 'parent')) return;
      toast({
        title: t('app.error'),
        description: translateError(error, t),
        variant: 'destructive',
      });
    }
  };

  // Unit conversion helpers
  const kgToLb = (kg: number) => (kg * 2.20462).toFixed(1);
  const cmToIn = (cm: number) => (cm / 2.54).toFixed(1);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('app.loading')}</p>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('patient.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/parent/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <img src="https://pub-327c49603f8c4373ac8b69237691fafc.r2.dev/rds-logo.png" alt="Logo" className="h-12 w-12" />
            <h1 className="text-2xl font-bold text-gray-900">🧒 {patient.name}</h1>
          </div>
          <div className="flex gap-2">
            <LanguageToggle />
            <Button variant="outline" onClick={handleDownloadPassport}>
              <Download className="h-4 w-4 mr-2" />
              {t('parent.downloadPassport')}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
            <TabsTrigger value="visits" className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              {t('visit.visits')}
            </TabsTrigger>
            <TabsTrigger value="vaccines" className="flex items-center gap-2">
              <Syringe className="h-4 w-4" />
              {t('vaccine.vaccines')}
            </TabsTrigger>
          </TabsList>

          {/* Visits Tab */}
          <TabsContent value="visits">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      {t('visit.visits')}
                    </CardTitle>
                    <CardDescription>
                      {t('visit.recentVisits')}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={useMetric ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setUseMetric(true)}
                    >
                      kg/cm
                    </Button>
                    <Button
                      variant={!useMetric ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setUseMetric(false)}
                    >
                      lb/in
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {visits.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('visit.noVisits')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {visits.map((visit) => (
                      <div
                        key={visit.id}
                        className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-3">
                            <div className="bg-green-100 p-2 rounded-full">
                              <Calendar className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">
                                {parseLocalDate(visit.visit_date).toLocaleDateString()}
                              </p>
                              <p className="text-sm text-gray-500">
                                {visit.doctor_name}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {visit.weight_kg && (
                            <div className="flex items-center gap-2">
                              <Scale className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-500">{t('visit.weight')}</p>
                                <p className="font-medium">
                                  {useMetric
                                    ? `${visit.weight_kg.toFixed(1)} kg`
                                    : `${kgToLb(visit.weight_kg)} lb`}
                                </p>
                              </div>
                            </div>
                          )}
                          {visit.height_cm && (
                            <div className="flex items-center gap-2">
                              <Ruler className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="text-sm text-gray-500">{t('visit.height')}</p>
                                <p className="font-medium">
                                  {useMetric
                                    ? `${visit.height_cm.toFixed(1)} cm`
                                    : `${cmToIn(visit.height_cm)} in`}
                                </p>
                              </div>
                            </div>
                          )}
                          {visit.doses && visit.doses.length > 0 && (
                            <div className="col-span-2">
                              <p className="text-sm text-gray-500">{t('visit.vaccinesAdministered')}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {visit.doses.map((dose, idx) => (
                                  <span
                                    key={`${visit.id}-${idx}`}
                                    className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                                  >
                                    {dose.vaccine_label} (#{dose.dose_number})
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {visit.parent_notes && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm text-gray-500">{t('visit.parentNotes')}</p>
                            <p className="text-sm">{visit.parent_notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vaccines Tab */}
          <TabsContent value="vaccines">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Syringe className="h-5 w-5" />
                  {t('vaccine.vaccines')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Upcoming Vaccines */}
                <div>
                  <h4 className="text-sm font-semibold mb-3">{t('vaccine.upcoming')}</h4>
                  {patient.upcoming_doses && patient.upcoming_doses.length > 0 ? (
                    <div className="space-y-2">
                      {patient.upcoming_doses.map((dose: any, idx: number) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            dose.is_overdue ? 'bg-red-50 border-red-200' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {dose.is_overdue && <AlertCircle className="h-4 w-4 text-red-500" />}
                            <span className={dose.is_overdue ? 'text-red-600 font-semibold' : ''}>
                              {dose.vaccine_label} - {t('vaccine.doseNumber')} {dose.dose_number}
                            </span>
                          </div>
                          {dose.due_date && (
                            <p className="text-sm text-gray-500 mt-1">
                              {t('vaccine.nextDueDate')}: {parseLocalDate(dose.due_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">{t('vaccine.noVaccines')}</p>
                  )}
                </div>

                {/* Completed Vaccines */}
                <div>
                  <h4 className="text-sm font-semibold mb-3">{t('parent.completedVaccines')}</h4>
                  {patient.completed_doses && patient.completed_doses.length > 0 ? (
                    <div className="space-y-2">
                      {patient.completed_doses.map((dose: any, idx: number) => (
                        <div key={idx} className="p-3 rounded-lg bg-green-50 border border-green-200">
                          <div className="flex items-center gap-2">
                            <span className="text-green-700">
                              ✓ {dose.vaccine_label} - {t('vaccine.doseNumber')} {dose.dose_number}
                            </span>
                          </div>
                          {dose.administered_at && (
                            <p className="text-sm text-gray-500 mt-1">
                              {t('vaccine.administeredDate')}: {parseLocalDate(dose.administered_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">{t('vaccine.noDosesRecorded')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
